import React, { FC } from 'react';
import TooltipWrapper from 'components/TooltipWrapper';
import { FaEdit } from 'react-icons/fa';
import FetchTable from 'components/table/FetchTable';
import { Column, CellProps } from 'react-table';
import Button from 'components/Button';
import { useHistory } from 'react-router';

import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';

import DeleteEntity from 'components/crud/DeleteEntity';
import { PermissionKeys } from 'store/types';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';
import InnerWrapper from '../../InnerWrapper';
import { IGroup } from '../types';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();

  const createGroup = () => {
    const url = buildClientUrl('/structure/groups/create', client);
    history.push(url);
  };

  const columns: Array<Column<IGroup>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.groups.name') || '',
      accessor: 'name',
      width: 200,
      minWidth: 150,
      Cell: (cell: CellProps<IGroup>) => {
        return <span className="text-break">{cell.value}</span>;
      },
    },
    {
      Header: t('structure.groups.address') || '',
      accessor: 'address',
      width: 'calc(50% - 200px)',
      minWidth: 200,
      disableSortBy: true,
    },
    {
      Header: t('structure.groups.description') || '',
      accessor: 'description',
      width: 'calc(50% - 120px)',
      minWidth: 200,
      disableSortBy: true,
    },
    {
      Header: () => <div className="text-right">{t('table.actions')}</div>,
      accessor: 'actions',
      width: 120,
      minWidth: 120,
      disableSortBy: true,
      Cell: (cell: CellProps<IGroup>) => {
        const { id, name } = cell.row.original;
        const url = buildClientUrl(`/structure/groups/edit/${id}`, client);
        const hasPermission = checkPermission(PermissionKeys.CAN_MANAGE_GROUPS);
        return (
          <div className="text-right">
            <div className="d-inline-block mr-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay={hasPermission ? t('common.edit') : t('common.noPermission')}
              >
                <Button
                  btnType="primary"
                  size="sm"
                  outlined
                  disabled={!hasPermission}
                  onClick={() => history.push(url)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
            <DeleteEntity
              id={id}
              entity="groups"
              successMsg={t('structure.groups.deleteSuccessMsg')}
              confirmHeading={t('structure.groups.deleteConfirmTitle')}
              confirmMsg={
                <span>
                  {t('structure.groups.deleteConfirmMsg')}&nbsp;
                  <span className="font-weight-bold">{name}</span>?
                </span>
              }
              queryKey={buildClientEntity('groups', client)}
              hasPermission={PermissionKeys.CAN_MANAGE_GROUPS}
            />
          </div>
        );
      },
    },
  ];

  return (
    <InnerWrapper>
      <FetchTable<IGroup>
        entityName={buildClientEntity('groups', client)}
        entityEndpoint="groups"
        columns={columns}
        hasPermission={[PermissionKeys.CAN_MANAGE_GROUPS]}
        onAdd={createGroup}
        addBtnLabel={t('structure.groups.addBtnLabel')}
        pagination
      />
    </InnerWrapper>
  );
};

export default Screen;
